// Tiny client-side sort utility. Tables that pre-load all rows can sort/filter
// in memory; we only call back to the server when the rowset itself changes.

export type SortDirection = 'asc' | 'desc';

export interface SortState<K extends string> {
  readonly key: K;
  readonly direction: SortDirection;
}

export function toggleSort<K extends string>(
  prev: SortState<K> | null,
  key: K,
): SortState<K> {
  if (!prev || prev.key !== key) return { key, direction: 'asc' };
  return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
}

// Stable comparator that handles strings, numbers, ISO dates, and nulls.
// Nulls/undefined sort last regardless of direction.
export function compareValues(a: unknown, b: unknown, direction: SortDirection): number {
  const aMissing = a === null || a === undefined || a === '';
  const bMissing = b === null || b === undefined || b === '';
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;

  let cmp = 0;
  if (typeof a === 'number' && typeof b === 'number') {
    cmp = a - b;
  } else {
    const aStr = String(a);
    const bStr = String(b);
    const aNum = Number(aStr);
    const bNum = Number(bStr);
    if (Number.isFinite(aNum) && Number.isFinite(bNum) && aStr !== '' && bStr !== '') {
      cmp = aNum - bNum;
    } else {
      cmp = aStr.localeCompare(bStr, undefined, { numeric: true, sensitivity: 'base' });
    }
  }
  return direction === 'asc' ? cmp : -cmp;
}

export function sortBy<T, K extends string>(
  rows: ReadonlyArray<T>,
  state: SortState<K> | null,
  accessor: (row: T, key: K) => unknown,
): ReadonlyArray<T> {
  if (!state) return rows;
  // Avoid mutating the input.
  return [...rows].sort((a, b) =>
    compareValues(accessor(a, state.key), accessor(b, state.key), state.direction),
  );
}
